import { createElement } from 'react';

import type { SiteInventoryHealth } from '@restDataAccess';

import { FlexAligner, Spinner, TrafficLight } from '@scaleaq/scaleui';

import { SITE_INVENTORY_SERVICES, type SiteServiceName } from '@/constants';
import { responsive } from '@/utils';

import { getIconByName } from '../../../getIconByName.ts';
import { siteInventoryHealthToTrafficLightType } from '../../siteSelector.utils.ts';
import { SiteCardName, SiteCardRoot } from './siteCard/siteCard.style.ts';

type SiteCardProps = {
  disabled?: boolean;
  health?: SiteInventoryHealth;
  isPending?: boolean;
  name: string;
  onClick: () => void;
  selected?: boolean;
  supportedServices?: readonly SiteServiceName[];
};

export const SiteCard = ({
  disabled = false,
  health,
  isPending = false,
  name,
  onClick,
  selected = false,
  supportedServices = [],
}: SiteCardProps) => {
  return (
    <SiteCardRoot
      type='button'
      $selected={selected}
      disabled={disabled}
      tabIndex={disabled ? -1 : 0}
      aria-pressed={selected}
      aria-disabled={disabled}
      onClick={disabled ? undefined : onClick}
    >
      <FlexAligner direction='column' gap={responsive.spacing['xsmall']} alignment={'top-left'}>
        <FlexAligner spaceBetween gap={responsive.spacing['2xlarge']} alignment={'center-right'}>
          <FlexAligner alignment='center-left' gap={responsive.spacing.large}>
            {health && (
              <TrafficLight type={siteInventoryHealthToTrafficLightType(health)} size='medium' />
            )}
            <SiteCardName type='heading_small' weight={500}>
              {name}
            </SiteCardName>
          </FlexAligner>
          <FlexAligner alignment={'center-right'} fullWidth={false}>
            {isPending && <Spinner />}
          </FlexAligner>
        </FlexAligner>
        <FlexAligner gap={responsive.spacing.medium} alignment={'center-left'}>
          {supportedServices.map(service => (
            <ServiceIcon key={service} service={service} />
          ))}
        </FlexAligner>
      </FlexAligner>
    </SiteCardRoot>
  );
};

type ServiceIconProps = {
  service: SiteServiceName;
};

/** Reuses the sidebar icon for the service's page, so a card badge cannot drift from the nav item. */
const ServiceIcon = ({ service }: ServiceIconProps) =>
  createElement(getIconByName(SITE_INVENTORY_SERVICES[service].pageName));
